'use client';

import { useState, useEffect } from 'react';
import Image from 'next/image';
import { useEnsAvatar, useEnsName } from 'wagmi';
import Avatar from 'boring-avatars';
import { useAtom, useAtomValue } from 'jotai';
import { userInfoAtom } from '@/app/atoms/users';
import { UniversalAvatarProps } from '../types/account';
import { generateSeed } from '@/app/helpers/generateSeed';
import ClipboardCopy from './ClipboardCopy';
import { ShieldCheckIcon } from '@heroicons/react/16/solid';
import { UsersAccountReturnType } from '@/app/types/account';
import { formatVotingPower } from '../helpers/formatVotingPower';
import { tokenInfoAtom } from '@/app/atoms/token';
import ClipboardCopyWithHiddenAddress from './ClipboardCopyWithHiddenAddress';

interface UniversalAvatarPropsWithSize extends UniversalAvatarProps {
  width?: number;
  height?: number;
  size?: number;
  showTokenHolder?: boolean;
  tokenBalance?: string;
  showTokenBalance?: boolean;
}

export const UniversalAvatar: React.FC<UniversalAvatarPropsWithSize> = ({
  address,
  width = 30,
  height = 30,
  size = 25,
  showTokenHolder = false,
  tokenBalance = '0',
  showTokenBalance = false,
}) => {
  const seed = generateSeed(address as `0x${string}`);
  const [users] = useAtom(userInfoAtom);
  const tokenInfo = useAtomValue(tokenInfoAtom);
  const { symbol } = tokenInfo;
  const [settings, setSettings] = useState<UsersAccountReturnType | undefined>();
  const chainId = Number(process.env.NEXT_PUBLIC_CHAIN_ID);
  const { data: ensName } = useEnsName({
    chainId: chainId,
    address: address as `0x${string}`,
  });

  const { data: ensAvatar } = useEnsAvatar({
    chainId: chainId,
    name: ensName!,
  });

  useEffect(() => {
    const newSettings = users.find(
      (user: UsersAccountReturnType) => user.wallet_address === address,
    );
    setSettings(newSettings);
  }, [address, users]);

  return (
    <div className="flex flex-col">
      <div className="flex gap-2">
        <div>
          {settings?.avatar_url ? (
            <Image
              src={settings.avatar_url}
              alt="Avatar"
              width={width}
              height={height}
              className="rounded-full z-20 border border-neutral-300 my-auto"
              style={{ width: `${width}px`, height: `${height}px` }}
            />
          ) : ensAvatar ? (
            <Image
              src={ensAvatar}
              alt="Avatar"
              width={width}
              height={height}
              className="rounded-full z-20 my-auto"
              style={{ width: `${width}px`, height: `${height}px` }}
            />
          ) : (
            <Avatar
              size={size}
              name={seed}
              variant="beam"
              colors={[
                '#0782b7',
                '#ed2e94',
                '#fa9005',
                '#083ff4d4',
                '#3ec300',
                '#f4f807',
                '#b01eca',
              ]}
            />
          )}
        </div>
        <div className={`flex flex-col ${!showTokenHolder && 'justify-center'}`}>
          <div className="flex cursor-default" title={address}>
            {settings?.profile_name ? (
              <ClipboardCopyWithHiddenAddress
                address={address}
                profileName={settings?.profile_name}
              />
            ) : ensName ? (
              <ClipboardCopyWithHiddenAddress address={address} ensName={ensName} />
            ) : (
              <ClipboardCopy address={address} truncate="64" />
            )}
          </div>
          <>
            {showTokenHolder && (
              <p className="text-xs flex items-center mt-1">
                <ShieldCheckIcon className="h-4 w-4 text-accent mr-1" />
                Token Owner
              </p>
            )}
            {showTokenBalance && (
              <div className="text-xs flex items-center mt-1 ml-1">
                {formatVotingPower(tokenBalance)} {symbol}s
              </div>
            )}
          </>
        </div>
      </div>
    </div>
  );
};

export default UniversalAvatar;
